import React, { useState, useEffect } from 'react'
import Interceptor from '../../../../Services/serviceBase.interceptor'
import { useErrorSnackBarContext } from '../../../Shared/ErrorSnackBar/ErrorSnackBarContext'
import LoadingSpinner from '../../../Shared/LoadingSpinner/LoadingSpinner'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import './resources.scss'
import { SimpleTreeView as TreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { Launch } from '@mui/icons-material'
import resourcesActions from './resourcesActions'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

const TreeItemEndIcon = () => <Launch style={{ height: '20px' }} />

const CustomTreeItemLabel = ({ node, onLinkClick }) => {
  if (node.url) {
    return (
      <span
        onClick={(e) => {
          e.stopPropagation()
          onLinkClick(node)
        }}
      >
        {node.description}
      </span>
    )
  }
  return <span>{node.description}</span>
}

function Resources() {
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState([])
  const [selectedNode, setSelectedNode] = useState([])
  const [resources, setResources] = useState(null)
  const [parentNodesIds, setParentNodesIds] = useState([])

  const { errorSnackBarActions } = useErrorSnackBarContext()
  Interceptor.interceptor(errorSnackBarActions)

  useEffect(() => {
    ;(async () => {
      const resources = await resourcesActions.getResources()

      if (resources) setResources(resources)
      //TODO: Handle error
      setLoading(false)
    })()
  }, [])

  useEffect(() => {
    if (resources?.resource) {
      const ids = collectParentNodes(resources.resource)
      setParentNodesIds(ids)
    }
  }, [resources])

  const collectParentNodes = (nodes, acc = []) => {
    if (!nodes) return acc

    nodes.forEach((node, i) => {
      const id = `${node.description}_${i}`

      if (node.children && node.children.length > 0) {
        acc.push(id)
        collectParentNodes(node.children, acc)
      }
    })

    return acc
  }

  const clickElement = (node) => {
    window.open(`${node.url}`)
  }

  const handleSelect = (event, nodeIds) => {
    setSelectedNode(nodeIds)
  }

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds)
  }

  const renderTree = (resources) =>
    resources && resources.length > 0
      ? resources.map((resource, i) => getTreeItemContent(resource, i))
      : getTreeItemContent(resources, 0)

  const getTreeItemContent = (resource, i) => (
    <TreeItem
      key={`${resource.description}_${i}`}
      itemId={`${resource.description}_${i}`}
      label={<CustomTreeItemLabel node={resource} onLinkClick={clickElement} />}
      classes={resource.url ? { content: 'treeItem-link' } : { content: 'treeItem-text' }}
    >
      {resource.children && Array.isArray(resource.children) ? renderTree(resource.children) : null}
    </TreeItem>
  )

  const handleFullCollapse = () => {
    setExpanded([])
  }

  const handleFullExpand = () => {
    setExpanded(parentNodesIds)
  }

  return (
    <div className="resourcesContent">
      <div className="page-header" id="implementationResourcesHEader">
        <span id="implementationResourcesId">Implementation Resources</span>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Container maxWidth="lg">
          <Grid container spacing={2} direction="column">
            <Grid size={12}>
              <div className="page-description">
                The key to the success of your MyLS system will be how well you communicate and roll
                it out to your community. Your community needs to know about MyLS, how to access it,
                and how to add your entity to their MyLS app. Communicating with them clearly and
                regularly about this will be very important. To help you with this, we are providing
                resources, templates and images for you to use. Please start with the file below
                called “Implementation Overview,” and then download and use the other files and
                images that are best suited for your community. If you draft and use other images,
                posts, or communications that you find to be successful, please share those with us
                so that we can pass them along to other MyLS users for their consideration and
                possible use. Those can be sent to{' '}
                <a href="mailto:sales@emergencysmc.com">sales@emergencysmc.com</a>. Thank you.
              </div>
            </Grid>

            <Grid size={12}>
              <Box
                className="expand-collapse-container"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  width: '100%'
                }}
              >
                <Button
                  variant="outlined"
                  id="collapseButton"
                  color="primary"
                  size="medium"
                  onClick={handleFullCollapse}
                >
                  Collapse all
                </Button>
                <Button
                  variant="contained"
                  id="expandButton"
                  color="primary"
                  size="medium"
                  onClick={handleFullExpand}
                >
                  Expand all
                </Button>
              </Box>
            </Grid>

            <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <TreeView
                sx={{ width: '100%' }}
                aria-label="resources items"
                slots={{
                  collapseIcon: ExpandMoreIcon,
                  expandIcon: ChevronRightIcon,
                  endIcon: TreeItemEndIcon
                }}
                expandedItems={expanded}
                selectedItems={selectedNode}
                onSelectedItemsChange={handleSelect}
                onExpandedItemsChange={handleToggle}
              >
                {renderTree(resources.resource)}
              </TreeView>
            </Grid>
          </Grid>
        </Container>
      )}
    </div>
  )
}

export default Resources
